import { ReactElement, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { notification, Typography } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { queryClient } from 'common/clients/query.client';
import { Button } from 'common/components/antd/Button/Button';
import { Checkbox } from 'common/components/antd/Checkbox/Checkbox';
import { Form } from 'common/components/antd/Form/Form';
import { Space } from 'common/components/antd/Space/Space';
import { Tabs } from 'common/components/antd/Tabs/Tabs';
import { EnhancedSelectOptions } from 'common/components/EnhancedSelect/EnhancedSelect';
import { ENV_API_TYPE, ENV_ASSET_CODE, ENV_ASSET_ISSUER, ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { IS_ADMIN_FE } from 'common/constants/general-access-role.constants';
import { EApiType } from 'common/enums/api-type.enum';
import { makeHumanReadablePopupError } from 'common/utils/error.util';
import {
  useCounterpartyAccountDetailsQuery,
  useCounterpartyAccountsListQuery,
  useCustomerAccountsListMutation,
} from 'domains/accounts/queries/accounts.query';
import {
  TAccountEntity,
  TAccountCustomersResData,
  TCustomerAccountEntity,
} from 'domains/accounts/types/accounts.types';
import { useGetAssetsListMutation } from 'domains/assets/queries/assets.query';
import { counterpartyQuery, useGetCounterpartiesQuery } from 'domains/counterparties/queries/counterparty.query';
import { TCounterpartiesListResData } from 'domains/counterparties/types/counterparty.types';
import { useGetCounterpartyCustomersListQuery } from 'domains/customers/queries/customers.query';
// import { MutedText } from 'domains/payments/components/MutedText/MutedText';
import { SwapButton } from 'domains/payments/components/SwapButton/SwapButton';
import {
  COUNTERPARTY_TYPE_OPTIONS,
  SHOULD_SHOW_SCHEDULED_PAYMENT,
} from 'domains/payments/constants/payments.constants';
import { ECounterpartyType } from 'domains/payments/enums/payments.enums';
import { useConfirmPaymentMutation, useCreatePaymentMutation } from 'domains/payments/queries/payments.query';
import {
  TConfirmPaymentReqData,
  TRecipientInitialData,
  TSenderInitialData,
} from 'domains/payments/types/payment.types';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import { EPaymentsFormItemName, ERecipientActionTypes, ESenderActionTypes } from './enums/payment-operation.enum';
import {
  SContainer,
  SFormContainer,
  STabWrapper,
  SSwapContainer,
  SSelectGroup,
  SEnhancedSelect,
  SDivider,
  SDatePicker,
  STimePicker,
  SInputNumber,
  SText2,
  SText3,
  SGroup,
  SSelectRow,
} from './payment-operation.page.style';
import {
  applyAssetToOption,
  filterFn,
  filterRecipientFn,
  makeAccountOption,
  makeAccountsOption,
  makeCounterPartyOption,
  makeCustomerOption,
  reducerRecipient,
  reducerSender,
} from './services/payment-operation.service';
import { TAccount, TSchedule } from './types/payment-operation.types';

export const PaymentOperationPage = (): ReactElement => {
  const { t } = useTranslation();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const [formLoading, setFormLoading] = useState<boolean>(false);
  const [tabState, setTabState] = useState<string>(state?.tabState || 'internal');
  const isExternalType = tabState === 'external';

  const initialData = {
    wallet_user_guid: '',
    accountGUID: null,
    iban: '',
    asset: {
      activated: false,
      balance: 0,
      code: '',
      minor_unit: 0,
      name: '',
      platform: '',
      stellar_code: '',
      stellar_issuer: '',
    },
  };

  const accountInitialData: TAccountCustomersResData = {
    accounts: [],
    total: 0,
  };

  const formInitialData = {
    [EPaymentsFormItemName.Amount]: 0,
  };

  const senderInitialData: TSenderInitialData = state?.sender || {
    senderAccountData: initialData,
    senderAccountValue: null,
    senderAsset: null,
    searchSenderValue: '',
    searchSenderAccountValue: '',
    searchSenderCounterpartyValue: '',
    isSenderCustomerSelected: false,
    selectSenderAccountsData: accountInitialData,
    senderBalanceVisible: true,
  };

  const recipientInitialData: TRecipientInitialData = state?.recipient || {
    recipientAccountData: initialData,
    recipientAccountValue: null,
    recipientAsset: null,
    searchRecipientValue: '',
    searchRecipientAccountValue: '',
    searchRecipientCounterpartyValue: '',
    isRecipientCustomerSelected: false,
    selectRecipientAccountsData: accountInitialData,
    recipientBalanceVisible: true,
  };

  // TODO: let
  let amount: TAccount = {
    value: null,
    setValue: (f) => f,
    onChange: (f) => f,
    disabled: true,
  };

  let schedule: TSchedule = {
    value: false,
    setValue: (f) => f,
    onChange: (f) => f,
    disabled: true,
  };

  const [stateSender, dispatchSender] = useReducer(reducerSender, senderInitialData);
  const [stateRecipient, dispatchRecipient] = useReducer(reducerRecipient, recipientInitialData);
  const [amountValue, setAmountValue] = useState<null | string | number>(null);
  const [scheduleValue, setScheduleValue] = useState<boolean>(false);
  const [fee, setFee] = useState<number>(0);

  const clearAllFormFields = (sender: boolean, receiver: boolean): void => {
    form.setFields([
      ...(sender
        ? [
            {
              name: EPaymentsFormItemName.SenderType,
              value: null,
            },
            {
              name: EPaymentsFormItemName.SenderValue,
              value: null,
            },
            {
              name: EPaymentsFormItemName.SenderDetailAccounts,
              value: null,
            },
          ]
        : []),
      ...(receiver
        ? [
            {
              name: EPaymentsFormItemName.RecipientType,
              value: null,
            },
            {
              name: EPaymentsFormItemName.RecipientValue,
              value: null,
            },
            {
              name: EPaymentsFormItemName.RecipientDetailAccounts,
              value: null,
            },
          ]
        : []),
    ]);
  };

  const goToTab = (key: string): void => {
    setTabState(key);
    dispatchRecipient({ type: ERecipientActionTypes.RecipientOnClear });
    form.setFields([
      {
        name: EPaymentsFormItemName.SenderCounterparty,
        value: form.getFieldValue(EPaymentsFormItemName.SenderCounterparty),
      },
      {
        name: EPaymentsFormItemName.SenderType,
        value: form.getFieldValue(EPaymentsFormItemName.SenderType),
      },
      {
        name: EPaymentsFormItemName.SenderValue,
        value: form.getFieldValue(EPaymentsFormItemName.SenderValue),
      },
      {
        name: EPaymentsFormItemName.SenderDetailAccounts,
        value: form.getFieldValue(EPaymentsFormItemName.SenderDetailAccounts),
      },
      {
        name: EPaymentsFormItemName.RecipientCounterparty,
        value: null,
      },
      {
        name: EPaymentsFormItemName.RecipientType,
        value: null,
      },
      {
        name: EPaymentsFormItemName.RecipientValue,
        value: null,
      },
      {
        name: EPaymentsFormItemName.RecipientDetailAccounts,
        value: null,
      },
      {
        name: EPaymentsFormItemName.Amount,
        value: 0,
      },
    ]);
    setFee(0);
    setAmountValue(0);
  };

  const { mutateAsync: mutateAsyncGetCustomerAccountsMutation } = useCustomerAccountsListMutation();
  const { counterparties } = useLoaderData() as { counterparties: TCounterpartiesListResData };

  const {
    data: counterpartiesQuery,
    isFetched: isCounterpartiesFetched,
    isLoading: isCounterpartiesLoading,
    isError: isCounterpartiesDataError,
    error: counterpartiesDataError,
  } = useGetCounterpartiesQuery(
    {},
    {
      initialData: counterparties,
      refetchOnMount: false,
    },
  );

  useEffect(() => {
    if (ENV_API_TYPE === EApiType.Bank) {
      const defaultCounterparty = counterpartiesQuery?.counterparties.find(
        (el) => el.guid === ENV_COUNTERPARTY_GUID,
      )?.guid;
      form.setFields([
        {
          name: EPaymentsFormItemName.SenderCounterparty,
          value: defaultCounterparty,
        },
      ]);
    }
  }, [isCounterpartiesFetched]);

  useEffect(() => {
    form.setFields([
      {
        name: EPaymentsFormItemName.SenderCounterparty,
        value: IS_ADMIN_FE ? state?.sender_counterparty : ENV_COUNTERPARTY_GUID,
      },
      {
        name: EPaymentsFormItemName.SenderType,
        value: state?.sender_type,
      },
      {
        name: EPaymentsFormItemName.SenderValue,
        value: state?.sender_value,
      },
      ...(state?.sender_detail_accounts
          ? [
            {
              name: EPaymentsFormItemName.SenderDetailAccounts,
              value: state?.sender_detail_accounts,
            }
          ]
          : []
      ),
      {
        name: EPaymentsFormItemName.RecipientCounterparty,
        value: state?.recipient_counterparty,
      },
      {
        name: EPaymentsFormItemName.RecipientType,
        value: state?.recipient_type,
      },
      {
        name: EPaymentsFormItemName.RecipientValue,
        value: state?.recipient_value,
      },
      ...(state?.recipient_detail_accounts
          ? [
            {
              name: EPaymentsFormItemName.RecipientDetailAccounts,
              value: state?.recipient_detail_accounts,
            }
          ]
          : []
      ),
      {
        name: EPaymentsFormItemName.Amount,
        value: state?.amount_value,
      },
    ]);
    setFee(state?.fee);
    setAmountValue(state?.amount_value);
  }, []);

  const senderCounterparty = {
    value: form.getFieldValue(EPaymentsFormItemName.SenderCounterparty),
    disabled: false,
    onClear: (): void => {
      amount.setValue(null);
      schedule.setValue(false);
      dispatchSender({ type: ESenderActionTypes.SenderCounterpartyOnClear });
      dispatchRecipient({ type: ERecipientActionTypes.RecipientCustomerSelect, payload: false });
    },
    options: counterpartiesQuery?.counterparties.map(makeCounterPartyOption) || [],
    onChange: (): void => {
      dispatchRecipient({ type: ERecipientActionTypes.RecipientCustomerSelect, payload: false });
    },
  };

  const {
    data: senderCounterpartyAccountsQuery,
    isError: isSenderCounterpartyAccountsDataError,
    error: senderCounterpartyAccountsDataError,
  } = useCounterpartyAccountsListQuery(
    senderCounterparty.value,
    {},
    {
      enabled: !!senderCounterparty.value,
      refetchOnMount: false,
    },
  );

  const {
    data: senderCounterpartyCustomersQuery,
    isError: isSenderCounterpartyCustomersDataError,
    error: senderCounterpartyCustomersDataError,
  } = useGetCounterpartyCustomersListQuery(
    senderCounterparty.value,
    {},
    {
      enabled: !!senderCounterparty.value,
      refetchOnMount: false,
    },
  );

  const isErrorSender =
    isCounterpartiesDataError || isSenderCounterpartyAccountsDataError || isSenderCounterpartyCustomersDataError;
  const errorSender =
    counterpartiesDataError || senderCounterpartyAccountsDataError || senderCounterpartyCustomersDataError;

  useEffect(() => {
    if (isErrorSender) {
      notification.error({
        message: (errorSender as { message: string }).message,
        type: 'error',
      });
    }
  }, [isErrorSender, errorSender]);

  const senderType = {
    value: form.getFieldValue(EPaymentsFormItemName.SenderType),
    onChange: (): void => {
      dispatchSender({ type: ESenderActionTypes.SenderAccountData });
      dispatchSender({ type: ESenderActionTypes.SenderOnClear });
    },
    disabled: form.getFieldValue(EPaymentsFormItemName.SenderCounterparty) === null,
    options: COUNTERPARTY_TYPE_OPTIONS,
  };

  const makeSenderOptions = (): { label: string, value: string, render: () => ReactElement }[] => {
    let accounts: TAccountEntity[] = senderCounterpartyAccountsQuery?.accounts || [];
    if (senderType.value === ECounterpartyType.Bank) {
      accounts =
        senderCounterpartyAccountsQuery?.accounts?.filter(
          (account) => account.account_kind !== ECounterpartyType.Customer,
        ) || [];
    }

    let targetOptions = applyAssetToOption(
      accounts || [],
      form.getFieldValue(EPaymentsFormItemName.SenderValue),
      stateSender.senderAsset,
    ).map(makeAccountOption(true));

    if (senderType.value === ECounterpartyType.Customer) {
      targetOptions = (senderCounterpartyCustomersQuery?.customers || []).map(({ login, wallet_user_guid }) =>
        makeCustomerOption(
          login,
          wallet_user_guid,
          false,
          stateSender.senderAccountData?.asset,
          stateSender.senderAccountData?.wallet_user_guid,
        ),
      );

      if (stateSender.senderAccountData) {
        targetOptions = (senderCounterpartyCustomersQuery?.customers || []).map(({ login, wallet_user_guid }) =>
          makeCustomerOption(
            login,
            wallet_user_guid,
            stateSender.senderBalanceVisible,
            stateSender.selectSenderAccountsData?.asset || stateSender.senderAccountData?.asset,
            stateSender.selectSenderAccountsData?.wallet_user_guid || stateSender.senderAccountData?.wallet_user_guid,
          ),
        );
      } else {
        targetOptions = (senderCounterpartyCustomersQuery?.customers || []).map(({ login, wallet_user_guid }) =>
          makeCustomerOption(login, wallet_user_guid, false, initialData?.asset, wallet_user_guid),
        );
      }
    }
    return targetOptions || [];
  };

  const filterAsset = (accounts: TCustomerAccountEntity[]): TCustomerAccountEntity[] =>
    accounts.filter((accData) =>
      accData?.assets?.find(
        (asset) => asset?.stellar_issuer === ENV_ASSET_ISSUER && asset?.stellar_code === ENV_ASSET_CODE,
      ),
    );

  const sender = {
    value: form.getFieldValue(EPaymentsFormItemName.SenderValue),
    onChange: async (value: string): Promise<void> => {
      dispatchSender({ type: ESenderActionTypes.SenderAccountData });
      form.setFields([
        {
          name: EPaymentsFormItemName.SenderDetailAccounts,
          value: null,
        },
      ]);

      if (senderType.value === ECounterpartyType.Customer && value) {
        if (form.getFieldValue(EPaymentsFormItemName.SenderCounterparty)) {
          const customerAccountsList = await mutateAsyncGetCustomerAccountsMutation({
            counterpartyGUID: senderCounterparty.value,
            customerGUID: value,
          });
          const customersAcc = filterAsset(customerAccountsList.accounts);

          if (customersAcc) {
            dispatchSender({
              type: ESenderActionTypes.SelectSenderAccountData,
              payload: {
                accounts: customersAcc,
                total: customersAcc?.length,
              },
            });
          }

          if (customerAccountsList?.total > 1) {
            dispatchSender({ type: ESenderActionTypes.SenderCustomerSelect, payload: true });
            dispatchSender({ type: ESenderActionTypes.SenderBalanceVisible, payload: false });
          } else {
            dispatchSender({
              type: ESenderActionTypes.SenderAccountValue,
              payload: customerAccountsList?.accounts[0]?.guid,
            });
            if (value && customersAcc?.length > 0) {
              dispatchSender({
                type: ESenderActionTypes.SenderAccountData,
                payload: {
                  wallet_user_guid: value,
                  accountGUID: customersAcc[0]?.guid,
                  asset: { ...customersAcc[0]?.assets[0] },
                  iban: customersAcc[0]?.iban,
                },
              });
              dispatchSender({ type: ESenderActionTypes.SenderBalanceVisible, payload: true });
            }
          }
        }
      }
    },
    disabled: form.getFieldValue(EPaymentsFormItemName.SenderType) == null,
    onClear: (): void => {
      dispatchSender({ type: ESenderActionTypes.SenderOnClear });
      amount.setValue(null);
      schedule.setValue(false);
    },
    options: makeSenderOptions(),
  };

  const makeSenderAccountsOptions = (): { label: string, value: string, render: () => ReactElement }[] => {
    if (stateSender.selectSenderAccountsData?.accounts) {
      const customersAcc: TCustomerAccountEntity[] = filterAsset(stateSender.selectSenderAccountsData?.accounts);
      const targetOptions = (customersAcc || []).map(({ name, guid, assets, iban }) =>
        makeAccountsOption(name, guid, iban, true, assets[0] || initialData.asset),
      );
      return targetOptions;
    }
    return [];
  };

  const senderDetailAccounts = {
    value: form.getFieldValue(EPaymentsFormItemName.SenderDetailAccounts),
    onChange: (value: string): void => {
      dispatchSender({ type: ESenderActionTypes.SenderDetailAccountsOnChange, payload: value });
      const data: TCustomerAccountEntity[] = (stateSender.selectSenderAccountsData?.accounts || [])?.filter(
        (account: TCustomerAccountEntity) => account.guid === value,
      );
      dispatchSender({
        type: ESenderActionTypes.SenderAccountData,
        payload: {
          wallet_user_guid: form.getFieldValue(EPaymentsFormItemName.SenderValue) || '',
          accountGUID: data[0]?.guid,
          asset: { ...data[0]?.assets[0] },
          iban: data[0]?.iban,
        },
      });
    },
    disabled: false,
    onClear: (): void => {
      dispatchSender({ type: ESenderActionTypes.SelectSenderAccountDataOnClear });
    },
    options: makeSenderAccountsOptions(),
  };

  const { data: useCounterpartyAccountDetailsData } = useCounterpartyAccountDetailsQuery(
    senderCounterparty.value!,
    sender.value!,
    {},
    {
      enabled: !!sender.value && senderType.value === ECounterpartyType.Bank,
    },
  );

  useEffect(() => {
    if (useCounterpartyAccountDetailsData) {
      const senderAccAsset = useCounterpartyAccountDetailsData?.assets.find(
        (asset: any) => asset.stellar_issuer === ENV_ASSET_ISSUER && asset.stellar_code === ENV_ASSET_CODE,
      )!;
      dispatchSender({ type: ESenderActionTypes.SenderAsset, payload: senderAccAsset });
    }
  }, [useCounterpartyAccountDetailsData]);

  const recipientCounterparty = {
    value: form.getFieldValue(EPaymentsFormItemName.RecipientCounterparty),
    onClear: (): void => {
      form.setFields([
        {
          name: EPaymentsFormItemName.RecipientValue,
          value: null,
        },
        {
          name: EPaymentsFormItemName.RecipientType,
          value: null,
        },
        {
          name: EPaymentsFormItemName.RecipientDetailAccounts,
          value: null,
        },
      ]);
      dispatchRecipient({ type: ERecipientActionTypes.RecipientOnClear });
      amount.setValue(null);
      schedule.setValue(false);
    },
    disabled: [senderCounterparty, sender].some((model) => model.disabled),
    onChange: (): void => {
      dispatchRecipient({ type: ERecipientActionTypes.RecipientOnClear });
    },
    options: counterpartiesQuery?.counterparties.map(makeCounterPartyOption) || [],
  };

  const {
    data: recipientCounterpartyAccountsQuery,
    isError: isRecipientCounterpartyAccountsDataError,
    error: recipientCounterpartyAccountsDataError,
  } = useCounterpartyAccountsListQuery(
    recipientCounterparty.value!,
    {},
    {
      enabled: !!recipientCounterparty.value,
      refetchOnMount: false,
    },
  );

  const {
    data: recipientCounterpartyCustomersQuery,
    isError: isRecipientCounterpartyCustomersDataError,
    error: recipientCounterpartyCustomersDataError,
  } = useGetCounterpartyCustomersListQuery(
    recipientCounterparty.value!,
    {},
    {
      enabled: !!recipientCounterparty.value,
      refetchOnMount: false,
    },
  );

  const isErrorRecipient = isRecipientCounterpartyAccountsDataError || isRecipientCounterpartyCustomersDataError;
  const errorRecipient = recipientCounterpartyAccountsDataError || recipientCounterpartyCustomersDataError;

  useEffect(() => {
    if (isErrorRecipient) {
      notification.error({
        message: (errorRecipient as { message: string }).message,
        type: 'error',
      });
    }
  }, [isErrorRecipient, errorRecipient]);

  const recipientType = {
    value: form.getFieldValue(EPaymentsFormItemName.RecipientType),
    onChange: (): void => {
      dispatchRecipient({ type: ERecipientActionTypes.RecipientAccountData });
      dispatchRecipient({ type: ERecipientActionTypes.RecipientOnClear });
    },
    disabled: isExternalType
      ? form.getFieldValue(EPaymentsFormItemName.RecipientCounterparty) == null
      : !form.getFieldValue(EPaymentsFormItemName.SenderCounterparty),
    options: COUNTERPARTY_TYPE_OPTIONS,
  };

  const makeRecipientOptions = (): { label: string, value: string, render: () => ReactElement }[] => {
    let accounts: TAccountEntity[] =
      (isExternalType ? recipientCounterpartyAccountsQuery : senderCounterpartyAccountsQuery)?.accounts || [];

    if (recipientType.value === ECounterpartyType.Bank) {
      accounts =
        (isExternalType ? recipientCounterpartyAccountsQuery : senderCounterpartyAccountsQuery)?.accounts?.filter(
          (account) => account.account_kind !== ECounterpartyType.Customer,
        ) || [];
    }

    let targetOptions = applyAssetToOption(
      accounts || [],
      form.getFieldValue(EPaymentsFormItemName.RecipientValue),
      stateRecipient.recipientAsset,
    ).map(makeAccountOption(false));

    if (recipientType.value === ECounterpartyType.Customer) {
      targetOptions = (
        (isExternalType ? recipientCounterpartyCustomersQuery : senderCounterpartyCustomersQuery)?.customers || []
      ).map(({ login, wallet_user_guid }) =>
        makeCustomerOption(
          login,
          wallet_user_guid,
          false,
          stateRecipient.recipientAccountData?.asset,
          stateRecipient.recipientAccountData?.wallet_user_guid,
        ),
      );

      if (stateRecipient.recipientAccountData) {
        targetOptions = (
          (isExternalType ? recipientCounterpartyCustomersQuery : senderCounterpartyCustomersQuery)?.customers || []
        ).map(({ login, wallet_user_guid }) =>
          makeCustomerOption(
            login,
            wallet_user_guid,
            false,
            stateRecipient.recipientAccountData?.asset,
            stateRecipient.recipientAccountData?.wallet_user_guid,
          ),
        );
      }
    }

    return targetOptions || [];
  };

  const recipient = {
    value: form.getFieldValue(EPaymentsFormItemName.RecipientValue),
    onChange: async (value: string): Promise<void> => {
      dispatchRecipient({ type: ERecipientActionTypes.RecipientAccountData });

      form.setFields([
        {
          name: EPaymentsFormItemName.RecipientDetailAccounts,
          value: null,
        },
      ]);

      if (recipientType.value === ECounterpartyType.Customer && value) {
        if (recipientCounterparty.value !== null || senderCounterparty.value) {
          const customerAccountsList = await mutateAsyncGetCustomerAccountsMutation({
            counterpartyGUID: (isExternalType ? recipientCounterparty.value : senderCounterparty.value) as string,
            customerGUID: value,
          });

          const customersAcc = filterAsset(customerAccountsList.accounts);
          if (customersAcc) {
            dispatchRecipient({
              type: ERecipientActionTypes.SelectRecipientAccountData,
              payload: {
                accounts: customersAcc,
                total: customersAcc?.length,
              },
            });
          }

          if (customerAccountsList?.total > 1) {
            dispatchRecipient({ type: ERecipientActionTypes.RecipientCustomerSelect, payload: true });
          } else {
            dispatchRecipient({
              type: ERecipientActionTypes.RecipientAccountValue,
              payload: customerAccountsList?.accounts[0]?.guid,
            });

            if (value && customersAcc?.length > 0) {
              dispatchRecipient({
                type: ERecipientActionTypes.RecipientAccountData,
                payload: {
                  wallet_user_guid: value,
                  accountGUID: customerAccountsList?.accounts[0]?.guid,
                  asset: { ...customersAcc[0]?.assets[0] },
                  iban: customerAccountsList?.accounts[0]?.iban,
                },
              });
            }
          }
        }
      }
    },
    onClear: useCallback(() => {
      dispatchRecipient({ type: ERecipientActionTypes.RecipientOnClear });
      amount.setValue(null);
      schedule.setValue(false);
    }, []),
    disabled:
      [isExternalType && !form.getFieldValue(EPaymentsFormItemName.RecipientCounterparty), sender, senderCounterparty]
        .filter((model) => !!model)
        // @ts-expect-error
        .some((model) => model.disabled || model.value === null) ||
      (form.getFieldValue(EPaymentsFormItemName.RecipientType) === ECounterpartyType.Customer &&
        (isExternalType
          ? recipientCounterpartyCustomersQuery?.total === 0
          : senderCounterpartyCustomersQuery?.total === 0)),
    options: makeRecipientOptions(),
  };

  const makeRecipientAccountsOptions = (): { label: string, value: string, render: () => ReactElement }[] => {
    if (stateRecipient.selectRecipientAccountsData?.accounts) {
      const customersAcc: TCustomerAccountEntity[] = filterAsset(stateRecipient.selectRecipientAccountsData?.accounts);
      const targetOptions = (customersAcc || []).map(({ name, guid, assets, iban }) =>
        makeAccountsOption(name, guid, iban, false, assets[0] || initialData.asset),
      );
      return targetOptions;
    }
    return [];
  };

  const recipientDetailAccounts = {
    value: form.getFieldValue(EPaymentsFormItemName.RecipientDetailAccounts),
    onChange: (value: string): void => {
      dispatchRecipient({ type: ERecipientActionTypes.RecipientAccountValue, payload: value });
      const data: TCustomerAccountEntity[] = (stateRecipient.selectRecipientAccountsData?.accounts || [])?.filter(
        (account: TCustomerAccountEntity) => account.guid === value,
      );
      dispatchRecipient({
        type: ERecipientActionTypes.RecipientAccountData,
        payload: {
          wallet_user_guid: form.getFieldValue(EPaymentsFormItemName.RecipientValue) || '',
          accountGUID: data[0]?.guid,
          asset: { ...data[0]?.assets[0] },
          iban: data[0]?.iban,
        },
      });
    },
    disabled: false,
    onClear: (): void => {
      dispatchRecipient({ type: ERecipientActionTypes.RecipientDetailAccountsOnClear });
    },
    options: makeRecipientAccountsOptions(),
  };

  const {
    data: useCounterpartyAccountDetailsDataFromRecipient,
  } = useCounterpartyAccountDetailsQuery(
    isExternalType ? recipientCounterparty.value! : senderCounterparty.value!,
    recipient.value!,
    {},
    {
      enabled: !!recipient.value && recipientType.value === ECounterpartyType.Bank,
    },
  );

  useEffect(() => {
    if (useCounterpartyAccountDetailsDataFromRecipient) {
      const recipientAccAsset = useCounterpartyAccountDetailsDataFromRecipient?.assets.find(
        (asset: any) => asset.stellar_issuer === ENV_ASSET_ISSUER && asset.stellar_code === ENV_ASSET_CODE,
      )!;
      dispatchRecipient({ type: ERecipientActionTypes.RecipientAsset, payload: recipientAccAsset });
    }
  }, [useCounterpartyAccountDetailsDataFromRecipient]);

  amount = {
    value: amountValue,
    setValue: setAmountValue,
    onChange: (value: string | number | null): void => {
      setAmountValue(value);
    },
    disabled: [
      senderCounterparty,
      isExternalType && recipientCounterparty,
      sender,
      recipient,
      stateSender.isSenderCustomerSelected && senderDetailAccounts,
      stateRecipient.isRecipientCustomerSelected && recipientDetailAccounts,
    ]
      .filter((el) => !!el)
      .some((m) => m.value == null),
  };

  // TODO: change mocked data
  const feePercent = 2;
  const currency = stateSender.senderAsset?.stellar_code;

  useEffect(() => {
    const sum = (+(amount.value ?? 0) / 100) * feePercent;
    setFee(sum);
  }, [amountValue]);

  schedule = {
    value: scheduleValue,
    setValue: setScheduleValue,
    onChange: (e: CheckboxChangeEvent): void => {
      setScheduleValue(e.target.checked);
    },
    disabled: [senderCounterparty, isExternalType && recipientCounterparty, sender, recipient, amount]
      .filter((el) => !!el)
      // @ts-expect-error
      .some((m) => m.value == null),
  };

  const handleSenderSearch = (value: string): void => {
    dispatchSender({ type: ESenderActionTypes.SearchSenderValue, payload: value });
  };

  const handleSenderCounterpartySearch = (value: string): void => {
    dispatchSender({ type: ESenderActionTypes.SearchSenderCounterpartyValue, payload: value });
  };

  const handleSenderAccountSearch = (value: string): void => {
    dispatchSender({ type: ESenderActionTypes.SearchSenderAccountValue, payload: value });
  };

  const handleRecipientSearch = (value: string): void => {
    dispatchRecipient({ type: ERecipientActionTypes.SearchRecipientValue, payload: value });
  };

  const handleRecipientAccountSearch = (value: string): void => {
    dispatchRecipient({ type: ERecipientActionTypes.SearchRecipientAccountValue, payload: value });
  };

  const filteredSenderAccountOptions = filterFn(
    stateSender.searchSenderAccountValue,
    senderDetailAccounts.options,
    recipient.value,
  );

  const filteredSenderOptions = filterFn(
    stateSender.searchSenderValue,
    sender.options,
    form.getFieldValue(EPaymentsFormItemName.RecipientValue),
  );

  const filteredRecipientAccountOptions = filterFn(
    stateRecipient.searchRecipientAccountValue,
    recipientDetailAccounts.options,
    sender.value,
  );

  const filteredRecipientOptions = filterRecipientFn(
    stateRecipient.searchRecipientValue,
    recipient.options,
    sender.value,
  );
  const filteredSenderCounterpartyOptions = (): EnhancedSelectOptions => {
    let options: EnhancedSelectOptions = [];
    if (stateSender.searchSenderCounterpartyValue?.length > 0) {
      options = senderCounterparty.options.filter((option) =>
        option.label.toLowerCase().includes(stateSender.searchSenderCounterpartyValue.toLowerCase()),
      );
    } else {
      options = senderCounterparty.options.filter((option) =>
        option.value.toLowerCase().includes(stateSender.searchSenderCounterpartyValue.toLowerCase()),
      );
    }
    if (ENV_API_TYPE === EApiType.Bank) {
      options = options.filter((item) => item.value === ENV_COUNTERPARTY_GUID);
    }
    return options;
  };

  const filteredRecipientCounterpartyOptions = useMemo(
    () =>
      recipientCounterparty.options
        .filter((option) => option.value !== senderCounterparty.value)
        .map((option) => ({ ...option, secondaryLabel: '' })),
    [senderCounterparty.value, recipientCounterparty.options],
  );

  const swapAccounts = (): void => {
    if (senderType.value !== recipientType.value) {
      dispatchSender({ type: ESenderActionTypes.SwapAccounts, payload: stateRecipient });
      dispatchRecipient({ type: ERecipientActionTypes.SwapAccounts, payload: stateSender });
      form.setFields([
        {
          name: EPaymentsFormItemName.SenderType,
          value: form.getFieldValue(EPaymentsFormItemName.RecipientType),
        },
        {
          name: EPaymentsFormItemName.RecipientType,
          value: form.getFieldValue(EPaymentsFormItemName.SenderType),
        },
        {
          name: EPaymentsFormItemName.SenderValue,
          value: form.getFieldValue(EPaymentsFormItemName.RecipientValue),
        },
        {
          name: EPaymentsFormItemName.RecipientValue,
          value: form.getFieldValue(EPaymentsFormItemName.SenderValue),
        },
        {
          name: EPaymentsFormItemName.SenderDetailAccounts,
          value: form.getFieldValue(EPaymentsFormItemName.RecipientDetailAccounts),
        },
        {
          name: EPaymentsFormItemName.RecipientDetailAccounts,
          value: form.getFieldValue(EPaymentsFormItemName.SenderDetailAccounts),
        },
      ]);
    } else {
      dispatchSender({ type: ESenderActionTypes.Swap, payload: stateRecipient });
      dispatchRecipient({ type: ERecipientActionTypes.Swap, payload: stateSender });
      form.setFields([
        {
          name: EPaymentsFormItemName.SenderValue,
          value: form.getFieldValue(EPaymentsFormItemName.RecipientValue),
        },
        {
          name: EPaymentsFormItemName.RecipientValue,
          value: form.getFieldValue(EPaymentsFormItemName.SenderValue),
        },
        ...(EPaymentsFormItemName.SenderDetailAccounts
          ? [
              {
                name: EPaymentsFormItemName.SenderDetailAccounts,
                value: form.getFieldValue(EPaymentsFormItemName.RecipientDetailAccounts),
              },
              {
                name: EPaymentsFormItemName.RecipientDetailAccounts,
                value: form.getFieldValue(EPaymentsFormItemName.SenderDetailAccounts),
              },
            ]
          : []),
      ]);
    }
  };

  const onChangeFormHandler = (changedValues: Record<EPaymentsFormItemName, string>): void => {
    const key = Object.keys(changedValues)[0];
    if (key) {
      switch (key) {
        case EPaymentsFormItemName.SenderCounterparty:
          senderCounterparty.onChange();

          if (isExternalType) {
            clearAllFormFields(true, false);
          } else {
            clearAllFormFields(true, true);
          }
          break;
        case EPaymentsFormItemName.SenderType:
          senderType.onChange();
          form.setFields([
            {
              name: EPaymentsFormItemName.SenderValue,
              value: null,
            },
            {
              name: EPaymentsFormItemName.SenderDetailAccounts,
              value: null,
            },
          ]);
          break;
        case EPaymentsFormItemName.SenderValue:
          sender.onChange(changedValues[key]);
          break;
        case EPaymentsFormItemName.SenderDetailAccounts:
          senderDetailAccounts.onChange(changedValues[key]);
          break;
        case EPaymentsFormItemName.RecipientCounterparty:
          recipientCounterparty.onChange();
          if (isExternalType) {
            clearAllFormFields(false, true);
          }
          break;
        case EPaymentsFormItemName.RecipientType:
          recipientType.onChange();
          form.setFields([
            {
              name: EPaymentsFormItemName.RecipientValue,
              value: null,
            },
            {
              name: EPaymentsFormItemName.RecipientDetailAccounts,
              value: null,
            },
          ]);
          break;
        case EPaymentsFormItemName.RecipientValue:
          recipient.onChange(changedValues[key]);
          break;
        case EPaymentsFormItemName.RecipientDetailAccounts:
          recipientDetailAccounts.onChange(changedValues[key]);
          break;
        case EPaymentsFormItemName.Amount:
          amount.onChange(changedValues[key]);
          break;
        default:
          break;
      }
    }
  };

  const { mutateAsync: mutateAsyncGetAssetsListMutation } = useGetAssetsListMutation();

  const { mutateAsync: mutateAsyncCreatePaymentMutation } = useCreatePaymentMutation();

  const { mutateAsync: mutateAsyncConfirmPaymentMutation } = useConfirmPaymentMutation();

  const onFinishFormHandler = async (data: Record<EPaymentsFormItemName, string>): Promise<void> => {
    setFormLoading(true);
    const {
      sender_value,
      sender_counterparty,
      recipient_counterparty,
      recipient_value,
      amount_value,
      sender_detail_accounts,
      recipient_detail_accounts,
    } = data;

    let senderAccountGUID;
    if (senderType.value === ECounterpartyType.Bank) {
      senderAccountGUID = String(sender_value);
    } else if (!stateSender.isSenderCustomerSelected) {
      senderAccountGUID = stateSender.senderAccountValue;
    } else {
      senderAccountGUID = sender_detail_accounts;
    }

    let recipientAccountGUID;
    if (recipientType.value === ECounterpartyType.Bank) {
      recipientAccountGUID = String(recipient_value);
    } else if (!stateRecipient.isRecipientCustomerSelected) {
      recipientAccountGUID = stateRecipient.recipientAccountValue;
    } else {
      recipientAccountGUID = recipient_detail_accounts;
    }

    let status = 'success';
    try {
      const assetsData = await mutateAsyncGetAssetsListMutation({ counterpartyGUID: sender_counterparty });
      const senderAssets = assetsData?.stellar_assets;
      const uahAsset =
        senderAssets !== undefined
          ? senderAssets.find(
              (asset: { asset_code: string | undefined; asset_issuer: string | undefined }) =>
                asset.asset_code === ENV_ASSET_CODE && asset.asset_issuer === ENV_ASSET_ISSUER,
            ) || senderAssets[0]
          : {
              asset_code: ENV_ASSET_CODE,
              asset_issuer: ENV_ASSET_ISSUER,
            };

      const reqBody = {
        amount: Number(amount_value),
        asset_code: uahAsset.asset_code as string,
        asset_issuer: uahAsset.asset_issuer as string,
        receiver_id: recipientAccountGUID as string,
        sender_id: senderAccountGUID as string,
      };

      const { guid } = await mutateAsyncCreatePaymentMutation({
        params: reqBody,
        counterpartyGUID: sender_counterparty,
      });

      const confirmData: TConfirmPaymentReqData = { guid };
      if (recipient_counterparty) confirmData.bank_guid = recipient_counterparty;

      await mutateAsyncConfirmPaymentMutation({
        data: confirmData,
        senderCounterpartyGUID: sender_counterparty,
      });

      setFormLoading(false);
    } catch (error) {
      status = 'error';
      if (isAxiosError(error) && error.response?.data?.error) {
        searchParams.append(status, error.response.data.error);
        if (error.response?.status === 400) {
          if (!error.response?.data?.error.includes('underfunded')) {
            notification.error({
              message: t('apiClient.error.400.message'),
              description: makeHumanReadablePopupError(error.response?.data?.error, 132),
              duration: 10,
            });
          } else notification.destroy();
        }
      }
      setFormLoading(false);
    }
    navigate(`${ROUTES_MAPPING.PROTECTED.PAYMENT_STATUS}?${searchParams.toString()}`, {
      state: {
        status,
        data: {
          ...data,
          sender: stateSender,
          recipient: stateRecipient,
          senderAccountGUID,
          recipientAccountGUID,
          tabState,
          fee,
        },
      }
    });
  };

  const handleCopy = (value: string): void => {
    navigator.clipboard.writeText(value);
  };

  const handleKeyDown = (e: any, value: string): void => {
    if (e.key === 'c') {
      if ((e.ctrlKey && !e.metaKey) || (e.metaKey && !e.ctrlKey)) {
        handleCopy(value);
      }
    }
  };

  return (
    <SFormContainer>
      <STabWrapper>
        <Tabs
          activeKey={tabState}
          onChange={goToTab}
          items={[
            {
              label: t('paymentOperation.tabs.internalTransfers'),
              key: 'internal',
              children: null,
            },
            {
              label: t('paymentOperation.tabs.externalTransfers'),
              key: 'external',
              children: null,
            },
          ]}
        />
        <SContainer>
          <Form
            form={form}
            onValuesChange={onChangeFormHandler}
            onFinish={onFinishFormHandler}
            initialValues={formInitialData}
          >
            <div>
              {isExternalType &&
                <Typography.Text>{t('paymentOperation.form.label.senderCounterparty')}</Typography.Text>}
              <Form.Item name={EPaymentsFormItemName.SenderCounterparty} style={{ marginTop: isExternalType ? 8 : 0 }}>
                <SEnhancedSelect
                  allowClear
                  size="large"
                  onClear={senderCounterparty.onClear}
                  placeholder={t('paymentOperation.form.select.placeholder.counterparty')}
                  options={filteredSenderCounterpartyOptions()}
                  showSearch
                  filterOption={false}
                  onSearch={handleSenderCounterpartySearch}
                  disabled={ENV_API_TYPE === EApiType.Bank}
                  loading={isCounterpartiesLoading}
                />
              </Form.Item>
              <Space
                style={{
                  marginTop: 16,
                }}
                size={24}
                direction="vertical"
              >
                <SSelectGroup>
                  <Typography.Text>{t('paymentOperation.form.label.sender')}</Typography.Text>
                  <SSelectRow>
                    <Form.Item name={EPaymentsFormItemName.SenderType}>
                      <SEnhancedSelect
                        size="large"
                        disabled={senderType.disabled}
                        placeholder={t('paymentOperation.form.select.placeholder.counterpartyType')}
                        options={senderType.options}
                        style={{ width: '107px' }}
                      />
                    </Form.Item>
                    <Form.Item name={EPaymentsFormItemName.SenderValue} shouldUpdate>
                      <SEnhancedSelect
                        size="large"
                        style={{ width: '321px' }}
                        allowClear
                        onClear={sender.onClear}
                        disabled={sender.disabled}
                        placeholder={t('paymentOperation.form.select.placeholder.sender')}
                        options={filteredSenderOptions}
                        showSearch
                        filterOption={false}
                        onSearch={handleSenderSearch}
                        optionFilterProp="children"
                        onKeyDown={(e): void => {
                          handleKeyDown(e, form.getFieldValue(EPaymentsFormItemName.SenderValue));
                        }}
                      />
                    </Form.Item>
                  </SSelectRow>
                  {senderType.value === ECounterpartyType.Customer && stateSender.isSenderCustomerSelected && (
                    <SSelectRow>
                      <Form.Item name={EPaymentsFormItemName.SenderDetailAccounts}>
                        <SEnhancedSelect
                          size="large"
                          style={{
                            marginLeft: '107px',
                            width: '321px',
                          }}
                          allowClear
                          onClear={senderDetailAccounts.onClear}
                          disabled={senderDetailAccounts.disabled}
                          placeholder={t('paymentOperation.form.select.placeholder.account')}
                          options={filteredSenderAccountOptions}
                          showSearch
                          filterOption={false}
                          onSearch={handleSenderAccountSearch}
                        />
                      </Form.Item>
                    </SSelectRow>
                  )}
                </SSelectGroup>

                {isExternalType && (
                  <div>
                    <Typography.Text>{t('paymentOperation.form.label.recipientCounterparty')}</Typography.Text>
                    <div
                      style={{
                        marginTop: 8,
                      }}
                    >
                      <Form.Item name={EPaymentsFormItemName.RecipientCounterparty}>
                        <SEnhancedSelect
                          allowClear
                          size="large"
                          disabled={recipientCounterparty.disabled}
                          onClear={recipientCounterparty.onClear}
                          placeholder={t('paymentOperation.form.select.placeholder.counterparty')}
                          options={filteredRecipientCounterpartyOptions}
                        />
                      </Form.Item>
                    </div>
                  </div>
                )}

                <SSelectGroup>
                  {sender.value != null && recipient.value != null && !isExternalType && (
                    <SSwapContainer>
                      <SwapButton onClick={swapAccounts} />
                    </SSwapContainer>
                  )}
                  <Typography.Text>{t('paymentOperation.form.label.recipient')}</Typography.Text>
                  <SSelectRow>
                    <Form.Item name={EPaymentsFormItemName.RecipientType}>
                      <SEnhancedSelect
                        size="large"
                        style={{ width: '107px' }}
                        disabled={recipientType.disabled}
                        placeholder={t('paymentOperation.form.select.placeholder.counterpartyType')}
                        options={recipientType.options}
                      />
                    </Form.Item>
                    <Form.Item name={EPaymentsFormItemName.RecipientValue}>
                      <SEnhancedSelect
                        size="large"
                        style={{ width: '321px' }}
                        allowClear
                        onClear={recipient.onClear}
                        placeholder={t('paymentOperation.form.select.placeholder.recipient')}
                        disabled={recipient.disabled}
                        options={filteredRecipientOptions}
                        showSearch
                        filterOption={false}
                        onSearch={handleRecipientSearch}
                        onKeyDown={(e): void => {
                          handleKeyDown(e, form.getFieldValue(EPaymentsFormItemName.RecipientValue));
                        }}
                      />
                    </Form.Item>
                  </SSelectRow>

                  {recipientType.value === ECounterpartyType.Customer && stateRecipient.isRecipientCustomerSelected && (
                    <SSelectRow>
                      <Form.Item name={EPaymentsFormItemName.RecipientDetailAccounts}>
                        <SEnhancedSelect
                          size="large"
                          style={{
                            marginLeft: '107px',
                            width: '321px',
                          }}
                          allowClear
                          onClear={recipientDetailAccounts.onClear}
                          disabled={recipientDetailAccounts.disabled}
                          placeholder={t('paymentOperation.form.select.placeholder.account')}
                          options={filteredRecipientAccountOptions}
                          showSearch
                          filterOption={false}
                          onSearch={handleRecipientAccountSearch}
                        />
                      </Form.Item>
                    </SSelectRow>
                  )}
                </SSelectGroup>
                <div>
                  <Typography.Text>{t('paymentOperation.form.label.amount')}</Typography.Text>
                  <div>
                    <Form.Item name={EPaymentsFormItemName.Amount}>
                      <SInputNumber min={0} size="large" disabled={amount.disabled} placeholder="1000" />
                    </Form.Item>
                  </div>
                </div>

                {isExternalType && SHOULD_SHOW_SCHEDULED_PAYMENT && (
                  <div>
                    <Checkbox checked={schedule.value} disabled={schedule.disabled} onChange={schedule.onChange}>
                      {t('paymentOperation.form.checkbox.scheduleTransfer')}
                    </Checkbox>

                    {schedule.value && (
                      <SGroup>
                        <SDatePicker size="large" />
                        <STimePicker size="large" />
                      </SGroup>
                    )}
                  </div>
                )}

                {/* {!!fee && (
                  <div>
                    <Space size={8} direction="vertical">
                      <SGroup>
                        <SText2>{t('paymentOperation.form.label.paymentFee')}:</SText2>
                        <SText2>
                          {fee} {currency}
                        </SText2>
                      </SGroup>

                      <MutedText>{feePercent}%</MutedText>
                    </Space>
                  </div>
                )} */}
                <SDivider />
              </Space>
              <Space
                style={{
                  marginTop: 16,
                }}
                size={16}
                direction="vertical"
              >
                <SGroup>
                  <SText2>{t('paymentOperation.form.label.totalAmount')}:</SText2>
                  <SText3 strong>
                    {(+(amount.value ?? 0)).toFixed(2)} {currency}
                  </SText3>
                </SGroup>
                <SDivider />
                <Form.Item>
                  <Button
                    type="primary"
                    size="large"
                    block
                    disabled={amount.disabled || !amount.value || formLoading}
                    loading={formLoading}
                    htmlType="submit"
                  >
                    {t('paymentOperation.form.button.transfer')}
                  </Button>
                </Form.Item>
              </Space>
            </div>
          </Form>
        </SContainer>
      </STabWrapper>
    </SFormContainer>
  );
};

export const loader = async (): Promise<{ counterparties: TCounterpartiesListResData | {} }> => {
  try {
    const queries = { counterparties: counterpartyQuery() };
    const counterparties =
      queryClient.getQueryData<TCounterpartiesListResData>(queries.counterparties.queryKey) ??
      (await queryClient.fetchQuery<TCounterpartiesListResData>(queries.counterparties));

    return { counterparties };
  } catch (error) {
    console.error(error);

    return {
      counterparties: {},
    };
  }
};

export default PaymentOperationPage;
